/**
 * Icon Registry
 *
 * This module maps semantic icon names (IconName enum) to actual Lucide React components.
 * This is the ONLY file in the application that should import from 'lucide-react' directly.
 *
 * Adding a new icon:
 * 1. Import the Lucide component: `import { NewIcon } from 'lucide-react'`
 * 2. Add the semantic name to IconName enum in types.ts
 * 3. Map the enum value to the component in iconRegistry below
 *
 * @example
 * ```ts
 * // In types.ts
 * export enum IconName {
 *   MY_NEW_ICON = 'my-new-icon',
 * }
 *
 * // In this file
 * import { SomeIcon } from 'lucide-react'
 * export const iconRegistry: IconRegistry = {
 *   [IconName.MY_NEW_ICON]: SomeIcon,
 * }
 * ```
 */

import {
  // Navigation & Layout
  LayoutDashboard,
  Kanban,
  FolderOpen,
  Menu,
  Calendar,

  // User & Authentication
  User,
  Users,
  UserCheck,
  UsersRound,
  LogOut,
  Search,

  // Education & Learning
  Building2,
  GraduationCap,
  BookOpen,
  School,

  // UI Controls
  Check,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Minus,

  // Actions
  Save,
  Pencil,
  Trash2,
  Settings,
  Link2,
  CornerDownRight,

  // Status & Feedback
  AlertCircle,
  Info,
  Bell,
  Loader2,

  // Theme & Display
  Sun,
  Moon,
  Monitor,
  Palette,
  Sparkles,
  Gem,
  IceCream as IceCreamIcon,
  TreePine,
  Heart,
  Droplets,
  Leaf,
  Flower,
  Waves,
  Cloud,
  Flame,

  // Sizing & Layout Controls
  Maximize2,
  Minimize2,

  // Tags
  Tag,

  // Board Templates
  Copy,
  LayoutTemplate,
  RefreshCw,

  // Analytics
  Activity,

  // Jobs
  Zap,

  // Media
  Play,

  // Links & External
  ExternalLink,

  // Password visibility
  Eye,
  EyeOff,
  LucideIcon,
} from 'lucide-react';

import { IconName, IconRegistry } from './types';

/**
 * Central icon registry mapping semantic names to Lucide components.
 *
 * This registry ensures:
 * - Type-safe icon lookups (invalid names caught at compile time)
 * - Single source of truth for all icons
 * - Easy library migration (just update this file)
 * - Consistent icon usage across the application
 */
export const iconRegistry: IconRegistry = {
  // Navigation & Layout
  [IconName.DASHBOARD]: LayoutDashboard,
  [IconName.KANBAN]: Kanban,
  [IconName.FOLDER]: FolderOpen,
  [IconName.MENU]: Menu,
  [IconName.CALENDAR]: Calendar,

  // User & Authentication
  [IconName.USER]: User,
  [IconName.USERS]: Users,
  [IconName.USER_CHECK]: UserCheck,
  [IconName.USER_SEARCH]: UsersRound,
  [IconName.SEARCH]: Search,

  // Education & Learning
  [IconName.BUILDING]: Building2,
  [IconName.GRADUATION_CAP]: GraduationCap,
  [IconName.BOOK_OPEN]: BookOpen,
  [IconName.SCHOOL]: School,

  // UI Controls
  [IconName.CHECK]: Check,
  [IconName.CHEVRON_DOWN]: ChevronDown,
  [IconName.CHEVRON_UP]: ChevronUp,
  [IconName.CHEVRON_LEFT]: ChevronLeft,
  [IconName.CHEVRON_RIGHT]: ChevronRight,
  [IconName.X]: X,
  [IconName.PLUS]: Plus,
  [IconName.MINUS]: Minus,

  // Actions
  [IconName.EDIT]: Pencil,
  [IconName.LINK]: Link2,
  [IconName.SAVE]: Save,
  [IconName.SETTINGS]: Settings,
  [IconName.TRASH]: Trash2,
  [IconName.CORNER_DOWN_RIGHT]: CornerDownRight,

  // Status & Feedback
  [IconName.ALERT_CIRCLE]: AlertCircle,
  [IconName.INFO]: Info,
  [IconName.BELL]: Bell,

  // Theme & Display
  [IconName.SUN]: Sun,
  [IconName.MOON]: Moon,
  [IconName.MONITOR]: Monitor,
  [IconName.PALETTE]: Palette,
  [IconName.SPARKLES]: Sparkles,
  [IconName.GEM]: Gem,
  [IconName.ICE_CREAM]: IceCreamIcon,
  [IconName.TREE_PINE]: TreePine,
  [IconName.HEART]: Heart,
  [IconName.DROPLETS]: Droplets,
  [IconName.LEAF]: Leaf,
  [IconName.FLOWER]: Flower,
  [IconName.WAVES]: Waves,
  [IconName.CLOUD]: Cloud,
  [IconName.FLAME]: Flame,

  // Sizing Controls
  [IconName.A_LARGE]: Maximize2,
  [IconName.A_SMALL]: Minimize2,

  // Loading & Status
  [IconName.LOADER]: Loader2,

  // Tags & Organization
  [IconName.TAG]: Tag,

  // Auth Actions
  [IconName.LOG_OUT]: LogOut,

  // Board Templates
  [IconName.COPY]: Copy,
  [IconName.LAYOUT_TEMPLATE]: LayoutTemplate,
  [IconName.REFRESH]: RefreshCw,

  // Analytics
  [IconName.ACTIVITY]: Activity,

  // Jobs
  [IconName.ZAP]: Zap,

  // Media
  [IconName.PLAY]: Play,

  // Links & External
  [IconName.EXTERNAL_LINK]: ExternalLink,

  // Password visibility
  [IconName.EYE]: Eye,
  [IconName.EYE_OFF]: EyeOff,
};

/**
 * Helper function to retrieve an icon component from the registry.
 *
 * @param name - The semantic icon name
 * @returns The Lucide icon component, or null if not found
 *
 * @example
 * ```tsx
 * const IconComponent = getIconComponent(IconName.DASHBOARD);
 * if (IconComponent) {
 *   return <IconComponent size={24} />;
 * }
 * ```
 */
export function getIconComponent(name: IconName): LucideIcon | null {
  return iconRegistry[name] || null;
}

/**
 * Type export for external use of LucideIcon.
 * This allows components to type icon props without importing from lucide-react.
 */
export type { LucideIcon };
